const JIRA_BASE = '/jira-proxy';
const JIRA_PROJECT = 'OK';
const JIRA_BUG_TYPE = 'Bug';

export const JIRA_FIELDS = {
  SPRINT: 'customfield_10015',
  TEAM: 'customfield_10922',
  ASSIGNED_TO: 'customfield_10917',
  DESCRIPTION_BUG: 'customfield_10911',
  BE_IMPL_STEPS: 'customfield_10932',
  FE_IMPL_STEPS: 'customfield_10970',
};

const JIRA_CREDS_KEY = '__ddJiraCreds';
const JIRA_PARENT_KEY = '__ddJiraParent';

export function getStoredParent() {
  return localStorage.getItem(JIRA_PARENT_KEY) || '';
}

export function storeParent(key) {
  localStorage.setItem(JIRA_PARENT_KEY, key);
}

export function getStoredCreds() {
  return localStorage.getItem(JIRA_CREDS_KEY) || '';
}

export function storeCreds(emailAndToken) {
  localStorage.setItem(JIRA_CREDS_KEY, emailAndToken);
}

export function clearCreds() {
  localStorage.removeItem(JIRA_CREDS_KEY);
}

function getAuthHeader() {
  const creds = getStoredCreds();
  if (!creds) return null;
  return `Basic ${btoa(creds)}`;
}

function jiraFetch(path, options = {}) {
  const auth = getAuthHeader();
  if (!auth) return Promise.reject(new Error('No Jira credentials. Enter your email:token in the Jira panel.'));
  return fetch(`${JIRA_BASE}${path}`, {
    ...options,
    headers: {
      'Authorization': auth,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Atlassian-Token': 'no-check',
      ...(options.headers || {}),
    },
  });
}

export async function fetchUserByName(displayName) {
  const res = await jiraFetch(`/rest/api/3/user/search?query=${encodeURIComponent(displayName)}&maxResults=1`);
  if (!res.ok) throw new Error(`Jira user search ${res.status}: ${await res.text()}`);
  const users = await res.json();
  return users[0] ?? null;
}

export async function fetchCurrentUser() {
  const res = await jiraFetch('/rest/api/3/myself');
  if (!res.ok) throw new Error(`Jira ${res.status}: ${await res.text()}`);
  return res.json(); // { accountId, displayName, ... }
}

export async function fetchIssue(key) {
  const res = await jiraFetch(
    `/rest/api/3/issue/${encodeURIComponent(key)}`
  );
  if (!res.ok) throw new Error(`Jira ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function fetchActiveSprint() {
  const boardRes = await jiraFetch(`/rest/agile/1.0/board?projectKeyOrId=${encodeURIComponent(JIRA_PROJECT)}&type=scrum&maxResults=1`);
  if (!boardRes.ok) throw new Error(`Jira board ${boardRes.status}: ${await boardRes.text()}`);
  const boardData = await boardRes.json();
  const board = boardData.values?.[0];
  if (!board) return null;
  const sprintRes = await jiraFetch(`/rest/agile/1.0/board/${board.id}/sprint?state=active&maxResults=1`);
  if (!sprintRes.ok) throw new Error(`Jira sprint ${sprintRes.status}: ${await sprintRes.text()}`);
  const sprintData = await sprintRes.json();
  return sprintData.values?.[0] ?? null;
}

export async function createIssue(fields) {
  const res = await jiraFetch('/rest/api/3/issue', {
    method: 'POST',
    body: JSON.stringify({
      fields: {
        project: {
          key: JIRA_PROJECT
        },
        issuetype: {
          name: JIRA_BUG_TYPE
        },
        ...fields
      }
    }),
  });
  if (!res.ok) throw new Error(`Jira ${res.status}: ${await res.text()}`);
  return res.json(); // { id, key, self }
}

export async function linkIssues(newKey, parentKey) {
  const res = await jiraFetch('/rest/api/3/issueLink', {
    method: 'POST',
    body: JSON.stringify({
      type: { name: 'Relates' },
      inwardIssue: { key: newKey },
      outwardIssue: { key: parentKey },
    }),
  });
  if (!res.ok) throw new Error(`Jira link ${res.status}: ${await res.text()}`);
}

const TRAILING_PUNCT_RE = /[.,;:!?)>\]"']+$/;

function stepToInlineNodes(text) {
  const urlPattern = /https?:\/\/\S+/g;
  const nodes = [];
  let lastIndex = 0;
  let match;
  while ((match = urlPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', text: text.slice(lastIndex, match.index) });
    }
    const rawUrl = match[0];
    const trailMatch = rawUrl.match(TRAILING_PUNCT_RE);
    const url = trailMatch ? rawUrl.slice(0, -trailMatch[0].length) : rawUrl;
    nodes.push({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] });
    if (trailMatch) nodes.push({ type: 'text', text: trailMatch[0] });
    lastIndex = match.index + rawUrl.length;
  }
  if (lastIndex < text.length) nodes.push({ type: 'text', text: text.slice(lastIndex) });
  return nodes.length > 0 ? nodes : [{ type: 'text', text }];
}

export function buildDescriptionAdf(steps, replayUrl, { actualResult = '', expectedResult = '' } = {}) {
  const stepItems = steps.map(text => ({
    type: 'listItem',
    content: [{ type: 'paragraph', content: stepToInlineNodes(text) }],
  }));

  const content = [
    { type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: 'Steps to Reproduce' }] },
    ...(stepItems.length > 0
      ? [{ type: 'orderedList', content: stepItems }]
      : [{ type: 'paragraph', content: [{ type: 'text', text: '(no steps selected)' }] }]
    ),
    { type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: 'Session Replay' }] },
    {
      type: 'paragraph',
      content: replayUrl
        ? [{ type: 'inlineCard', attrs: { url: replayUrl } }]
        : [{ type: 'text', text: 'No replay link available.' }],
    },
    ...(actualResult ? [
      { type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: 'Actual Result' }] },
      { type: 'paragraph', content: [{ type: 'text', text: actualResult }] },
    ] : []),
    ...(expectedResult ? [
      { type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: 'Expected Result' }] },
      { type: 'paragraph', content: [{ type: 'text', text: expectedResult }] },
    ] : []),
  ];

  return { type: 'doc', version: 1, content };
}

function adfListItem(node, listDepth, bullet) {
  const indent = '  '.repeat(listDepth - 1);
  const children = Array.isArray(node.content) ? node.content.map(c => adfToText(c, listDepth)).join('') : '';
  return `${indent}${bullet} ${children}`;
}

function adfToText(node, listDepth = 0) {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  if (node.type === 'inlineCard') return node.attrs?.url ?? '';

  const content = Array.isArray(node.content) ? node.content : [];
  const mapChildren = () => content.map(c => adfToText(c, listDepth)).join('');

  switch (node.type) {
    case 'heading': return `${'#'.repeat(node.attrs?.level ?? 1)} ${mapChildren()}\n`;
    case 'paragraph': return `${mapChildren()}\n`;
    case 'bulletList': return content.map(item => adfListItem(item, listDepth + 1, '-')).join('');
    case 'orderedList': return content.map((item, i) => adfListItem(item, listDepth + 1, `${i + 1}.`)).join('');
    case 'codeBlock': return `\`\`\`\n${mapChildren()}\`\`\`\n`;
    case 'blockquote': return `> ${mapChildren()}\n`;
    case 'rule': return '---\n';
    default: return mapChildren();
  }
}

export async function fetchIssueForAi(key) {
  const issue = await fetchIssue(key);
  const f = issue.fields;

  const labels = Array.isArray(f.labels) ? f.labels : [];

  const links = Array.isArray(f.issuelinks)
    ? f.issuelinks.map(l => {
      const linked = l.outwardIssue ?? l.inwardIssue;
      const dir = l.outwardIssue ? l.type?.outward : l.type?.inward;
      return linked ? `${dir}: ${linked.key} — "${linked.fields?.summary ?? ''}"` : null;
    }).filter(Boolean)
    : [];

  const descriptionText = f.description ? adfToText(f.description).trim() : '';
  const beImplSteps = f[JIRA_FIELDS.BE_IMPL_STEPS] ? adfToText(f[JIRA_FIELDS.BE_IMPL_STEPS]).trim() : '';
  const feImplSteps = f[JIRA_FIELDS.FE_IMPL_STEPS] ? adfToText(f[JIRA_FIELDS.FE_IMPL_STEPS]).trim() : '';

  return {
    key: issue.key,
    summary: f.summary ?? '',
    labels,
    links,
    descriptionText,
    beImplSteps,
    feImplSteps,
  };
}
